"use client"
import { useState, useEffect } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { Search, Grid, List, Tag, RotateCcw } from "lucide-react"
import { Helmet } from "react-helmet-async"
import "./Products.css"
import placeholder from "../../images/placeholder.png"

const Products = () => {
  const [products, setProducts] = useState([])
  const [filteredProducts, setFilteredProducts] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [viewMode, setViewMode] = useState("grid")
  const [sortBy, setSortBy] = useState("name")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const navigate = useNavigate()
  const location = useLocation()

  const API_BASE_URL = "https://imperia-backend.onrender.com/api/products"
  const BEARER_TOKEN = "YWRtaW5AZXhhbXBsZS5jb206c3VwZXJzZWN1cmU="
  const [categories, setCategories] = useState([{ id: "all", name: "All Products" }])

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${BEARER_TOKEN}`,
    "Content-Type": "application/json",
  })

  const generateProductData = (product) => ({
    id: product.productId,
    name: product.name || "Premium Product",
    path: `/products/${product.productId}`,
    image: product.imageUrl || placeholder,
    description:
      product.description ||
      "High-quality product engineered for superior performance and reliability with advanced technology.",
    price: product.price || 0,
    category: product.category || "uncategorized",
    tags: product.tags || "",
  })

  // Helper to parse tags reliably regardless of data type
  const parseTags = (tags) => {
    if (!tags) return []
    if (typeof tags === "string") {
      return tags.split(",").map((tag) => tag.trim()).filter(Boolean)
    }
    if (Array.isArray(tags)) {
      return tags.map((tag) => (typeof tag === "string" ? tag.trim() : "")).filter(Boolean)
    }
    return []
  }

  // Utility: format category nicely
  const formatCategory = (cat) =>
    cat.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())

  // Utility: generate semantically grouped tags from all products (for better keywords)
  const generateSemanticTagGroups = (productsList) => {
    const allTagsSet = new Set()
    productsList.forEach((product) => {
      parseTags(product.tags).forEach((tag) => {
        allTagsSet.add(tag.toLowerCase())
      })
    })

    const allTagsArray = Array.from(allTagsSet)

    // Material tags - sample list, adapt as needed
    const materialTags = allTagsArray.filter((tag) =>
      ["steel", "rubber", "plastic", "metal", "aluminum"].some((mat) =>
        tag.includes(mat)
      )
    )

    // Application tags
    const applicationTags = allTagsArray.filter((tag) =>
      ["industrial", "commercial", "residential", "automotive", "marine"].some(
        (app) => tag.includes(app)
      )
    )

    // Feature tags
    const featureTags = allTagsArray.filter((tag) =>
      ["flexible", "durable", "pressure", "temperature", "resistant"].some(
        (fea) => tag.includes(fea)
      )
    )

    return { materialTags, applicationTags, featureTags, allTags: allTagsArray }
  }

  // Generate optimized meta keywords for products page
  const generatePageKeywords = (productsList) => {
    const { allTags } = generateSemanticTagGroups(productsList)

    // include categories and product names
    const categories = [
      ...new Set(productsList.map((p) => formatCategory(p.category))),
    ].slice(0, 10) // 10 max

    const productNames = [...new Set(productsList.map((p) => p.name))].slice(
      0,
      15
    )

    const keywords = new Set()

    // Include categories and product names
    categories.forEach((cat) => {
      keywords.add(cat)
      keywords.add(cat + " supplier")
      keywords.add("buy " + cat)
      keywords.add("best " + cat)
    })

    productNames.forEach((name) => {
      keywords.add(name)
      keywords.add(name + " price")
      keywords.add("buy " + name)
    })

    // Add tags (max 30)
    allTags.slice(0, 30).forEach((tag) => {
      keywords.add(tag)
      keywords.add("best " + tag)
      keywords.add(tag + " products")
    })

    return Array.from(keywords).slice(0, 60).join(", ")
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const urlSearchTerm = searchParams.get("search")
    if (urlSearchTerm) {
      setSearchTerm(urlSearchTerm)
    }
  }, [location.search])

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true)
      setError(null)

      try {
        const response = await fetch(API_BASE_URL, {
          method: "GET",
          headers: getAuthHeaders(),
        })
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const result = await response.json()

        if (result.success && result.data && Array.isArray(result.data)) {
          const productsData = result.data.map(generateProductData)
          setProducts(productsData)
          setFilteredProducts(productsData)
          // console.log(productsData)

          const uniqueCategories = [
            ...new Set(productsData.map((product) => product.category).filter(Boolean)),
          ]

          const categoryOptions = [
            { id: "all", name: "All Products" },
            ...uniqueCategories.map((cat) => ({
              id: cat,
              name: formatCategory(cat),
            })),
          ]

          setCategories(categoryOptions)
        } else {
          throw new Error("Invalid API response format")
        }
      } catch (error) {
        console.error("Error fetching products:", error)
        setError(error.message)
        setProducts([])
        setFilteredProducts([])
      } finally {
        setLoading(false)
      }
    }
    fetchProducts()
  }, [])

  useEffect(() => {
    let filtered = products
    if (selectedCategory !== "all") {
      filtered = products.filter(
        (product) => product.category === selectedCategory
      )
    }
    if (searchTerm) {
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.id.toString().includes(searchTerm)
      )
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "price-low":
          return a.price - b.price
        case "price-high":
          return b.price - a.price
        case "name":
        default:
          return a.name.localeCompare(b.name)
      }
    })
    setFilteredProducts(filtered)
  }, [searchTerm, selectedCategory, products, sortBy])

  const handleProductClick = (path) => {
    navigate(path)
  }

  // Breadcrumb structured data for JSON-LD
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: window.location.origin,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Products",
        item: `${window.location.origin}/products`,
      },
    ],
  }

  // Generate product list structured data for the filtered products shown
  const productListStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: filteredProducts.slice(0, 20).map((product, index) => ({
      "@type": "Product",
      position: index + 1,
      name: product.name,
      description: product.description,
      image: product.image,
      url: `${window.location.origin}${product.path}`,
      sku: product.id,
      category: formatCategory(product.category),
      offers: {
        "@type": "Offer",
        priceCurrency: "INR",
        price: product.price,
        availability: "https://schema.org/InStock",
        url: `${window.location.origin}${product.path}`,
      },
    })),
  }

  const { materialTags, applicationTags, featureTags, allTags } =
    generateSemanticTagGroups(products)

  const formattedCategories = categories
    .filter((c) => c.id !== "all")
    .map((cat) => formatCategory(cat.id))

  const pageTitle = "Products & Solutions - Imperia Hoses"
  const pageDescription = `Explore our premium range of products including categories such as ${formattedCategories.join(
    ", "
  )}. Find durable, high-quality, and advanced products for industrial, commercial, and residential uses.`

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={generatePageKeywords(products)} />
        <link rel="canonical" href={`${window.location.origin}/products`} />

        {/* Robots */}
        <meta
          name="robots"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
        <meta name="googlebot" content="index, follow" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={`${window.location.origin}/products`} />
        <meta property="og:site_name" content="Imperia Hoses" />
        <meta property="og:locale" content="en_IN" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:site" content="@ImperiaHoses" />
        <meta name="twitter:creator" content="@ImperiaHoses" />

        {/* Breadcrumb JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData)}
        </script>

        {/* Product list JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(productListStructuredData)}
        </script>
      </Helmet>

      <div className="products-page">
        {/* Hidden SEO semantic content block, invisible offscreen for Google indexing */}
        <div
          style={{
            position: "absolute",
            left: "-9999px",
            width: "1px",
            height: "1px",
            overflow: "hidden",
          }}
          aria-hidden="true"
        >
          <h1>Imperia Hoses Product Catalog</h1>
          <p>
            Browse our extensive catalog featuring {products.length} products in
            categories such as {formattedCategories.join(", ")}.
          </p>

          {/* Output tags and categories in hidden text for semantic indexing */}
          <p>
            Available materials: {materialTags.join(", ")}. Applicable for
            industries including {applicationTags.join(", ")}. Key features include{" "}
            {featureTags.join(", ")}.
          </p>

          {/* List product names and tags succinctly */}
          {products.slice(0, 25).map(({ name, tags }, i) => (
            <span key={i}>
              {name}{" "}
              {parseTags(tags).slice(0, 4).join(" ")}{" "}
              |
            </span>
          ))}
        </div>

        <section className="products-section">
          <div className="products-container">
            <div className="products-header">
              <div className="products-count" aria-live="polite" aria-atomic="true">
                Showing {filteredProducts.length} products
              </div>
               <div className="filters-controls">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="category-select"
                  aria-label="Select product category"
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <div className="view-toggle" role="group" aria-label="View mode toggle">
                  <button
                    className={`view-button ${viewMode === "grid" ? "active" : ""}`}
                    onClick={() => setViewMode("grid")}
                    aria-pressed={viewMode === "grid"}
                    aria-label="Grid view"
                  >
                    <Grid size={16} />
                  </button>
                  <button
                    className={`view-button ${viewMode === "list" ? "active" : ""}`}
                    onClick={() => setViewMode("list")}
                    aria-pressed={viewMode === "list"}
                    aria-label="List view"
                  >
                    <List size={16} />
                  </button>
                </div>
              </div>
            </div>
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner" role="status" aria-label="Loading products"></div>
                <p className="loading-text">Loading products...</p>
              </div>
            ) : error ? (
              <div className="empty-state" role="alert">
                <h3>Failed to load products</h3>
                <p>Error: {error}</p>
                <button onClick={() => window.location.reload()} className="btn-primary">
                  Retry
                </button>
              </div>
            ) : filteredProducts.length > 0 ? (
              <div className={`products-grid ${viewMode}-view`}>
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <h3>No products found</h3>
                <p>Try adjusting your search criteria or browse all products.</p>
                <button
                  onClick={() => {
                    setSearchTerm("")
                    setSelectedCategory("all")
                  }}
                  className="btn-primary"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  )

  // ProductCard component unchanged except tags parsing fix and added aria-label for accessibility
  function ProductCard({ product }) {
    const tagsArray = parseTags(product.tags).slice(0, 3)

    return (
      <article
        className={`product-card ${viewMode === "grid" ? "grid-card" : "list-card"}`}
        onClick={() => handleProductClick(product.path)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && handleProductClick(product.path)}
        aria-label={`View details for ${product.name}, category ${formatCategory(
          product.category
        )}, Tags: ${tagsArray.join(", ")}`}
      >
        {/* SEO hidden content */}
        <div
          style={{
            position: "absolute",
            left: "-9999px",
            width: "1px",
            height: "1px",
            overflow: "hidden",
          }}
          aria-hidden="true"
        >
          <h2>
            {product.name} - {tagsArray.join(" ")}
          </h2>
          <p>
            Buy {tagsArray.join(", ")} online. Best {tagsArray.join(" and ")} available.
            Shop {product.name} with {tagsArray.join(", ")} features.
          </p>
          {tagsArray.map((tag, index) => (
            <span key={index}>
              {tag} product {formatCategory(product.category)} buy online
            </span>
          ))}
        </div>

        <div className="product-image-container">
          <img
            src={product.image || placeholder}
            alt={product.name}
            onError={(e) => {
              e.target.src = placeholder
            }}
            className="product-image"
          />
          <div className="product-id-badge">
            <span>{product.id}</span>
          </div>
        </div>
        <div className="product-content">
          <div className="product-category-badge">
            <Tag size={12} />
            <span>{formatCategory(product.category)}</span>
          </div>
          <h3 className="product-title">{product.name}</h3>
          {/* SEO-Optimized Tags Display */}
          {tagsArray.length > 0 && (
            <div className="product-tags-container">
              {tagsArray.map((tag, index) => (
                <span
                  key={index}
                  className="product-tag-pill"
                  data-search-term={tag.toLowerCase()}
                  title={`${tag} - Premium quality ${formatCategory(product.category)}`}
                >
                  <span className="product-tag-dot"></span>
                  <span className="tag-text">{tag}</span>
                  {/* Hidden SEO text for each tag */}
                  <span
                    style={{
                      position: "absolute",
                      left: "-9999px",
                      fontSize: "1px",
                    }}
                  >
                    {tag} buy online best {tag} {formatCategory(product.category)} shop {tag} products
                  </span>
                </span>
              ))}
              {/* Additional hidden tag combinations */}
              <div
                style={{
                  position: "absolute",
                  left: "-9999px",
                  fontSize: "1px",
                }}
              >
                <span>buy {tagsArray.join(" ")}</span>
                <span>best {tagsArray.join(" and ")}</span>
                <span>shop {tagsArray.join(", ")} online</span>
                <span>{tagsArray.join(" ")} for sale</span>
                <span>premium {tagsArray.join(" ")} products</span>
              </div>
            </div>
          )}
          <div className="product-actions">
            <Link
              to={product.path}
              className="btn-primary"
              title={`Buy ${product.name} - ${tagsArray.join(", ")} - Best deals online`}
              aria-label={`Shop ${product.name} with ${tagsArray.join(" and ")} features`}
            >
              View Details
            </Link>
          </div>
        </div>
      </article>
    )
  }
}

export default Products
